import React, { useState } from 'react';
import './anchor-styles.css';

/**
 * Anchor Settings Component - Company Profile & Settings
 */
const AnchorSettings = ({ anchorProfile = {}, onUpdateProfile, loading }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    companyName: anchorProfile.companyName || '',
    gstNumber: anchorProfile.gstNumber || '',
    panNumber: anchorProfile.panNumber || '',
    address: anchorProfile.address || '',
    contactName: anchorProfile.contactName || '',
    contactEmail: anchorProfile.contactEmail || '',
    contactPhone: anchorProfile.contactPhone || '',
    creditLimit: anchorProfile.creditLimit || 0,
    availableCredit: anchorProfile.availableCredit || 0
  });

  const [paymentSettings, setPaymentSettings] = useState({
    defaultPaymentTerms: 30,
    autoApprovalEnabled: false,
    autoApprovalLimit: 100000,
    emailNotifications: true,
    smsNotifications: true,
    invoiceSubmissionAlert: true,
    approvalReminder: true,
    paymentDueAlert: true
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentSettings({
      ...paymentSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSaveProfile = () => {
    onUpdateProfile(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      companyName: anchorProfile.companyName || '',
      gstNumber: anchorProfile.gstNumber || '',
      panNumber: anchorProfile.panNumber || '',
      address: anchorProfile.address || '',
      contactName: anchorProfile.contactName || '',
      contactEmail: anchorProfile.contactEmail || '',
      contactPhone: anchorProfile.contactPhone || '',
      creditLimit: anchorProfile.creditLimit || 0,
      availableCredit: anchorProfile.availableCredit || 0
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="text-center" style={{ padding: '50px' }}>
          <i className="fa fa-spinner fa-spin fa-3x"></i>
          <p style={{ marginTop: '15px' }}>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="anchor-settings">
      <div className="settings-header">
        <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
          <i className="fa fa-cog"></i> Settings & Profile
        </h4>
      </div>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs" style={{ marginBottom: '20px' }}>
        <li className={activeTab === 'profile' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('profile'); }}>
            <i className="fa fa-building"></i> Company Profile
          </a>
        </li>
        <li className={activeTab === 'payment' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('payment'); }}>
            <i className="fa fa-credit-card"></i> Payment Settings
          </a>
        </li>
        <li className={activeTab === 'notifications' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('notifications'); }}>
            <i className="fa fa-bell"></i> Notifications
          </a>
        </li>
        <li className={activeTab === 'security' ? 'active' : ''}>
          <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('security'); }}>
            <i className="fa fa-lock"></i> Security
          </a>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Company Profile Tab */}
        {activeTab === 'profile' && (
          <div className="settings-section">
            <div className="section-header">
              <h5 style={{ fontWeight: '600' }}>Company Information</h5>
              {!isEditing && (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fa fa-edit"></i> Edit Profile
                </button>
              )}
            </div>

            <div className="row" style={{ marginTop: '20px' }}>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Company Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="companyName"
                    className="form-control"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>GST Number <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="gstNumber"
                      className="form-control"
                      value={formData.gstNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    />
                    <span className="input-group-addon">
                      <i className="fa fa-check-circle" style={{ color: '#4CAF50' }}></i> Verified
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>PAN Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="panNumber"
                    className="form-control"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    name="contactName"
                    className="form-control"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    className="form-control"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    name="contactPhone"
                    className="form-control"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="col-md-12">
                <div className="form-group">
                  <label>Registered Address</label>
                  <textarea
                    name="address"
                    className="form-control"
                    rows="3"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  ></textarea>
                </div>
              </div>
            </div>

            <hr />

            {/* Credit Information */}
            <div className="credit-info" style={{ marginTop: '20px' }}>
              <h5 style={{ fontWeight: '600', marginBottom: '15px' }}>Credit Information</h5>
              <div className="row">
                <div className="col-md-6">
                  <div className="info-box" style={{ backgroundColor: '#E3F2FD', padding: '20px', borderRadius: '8px' }}>
                    <div className="info-label">Total Credit Limit</div>
                    <div className="info-value" style={{ fontSize: '24px', fontWeight: '600', color: '#1976D2' }}>
                      ₹{formatAmount(formData.creditLimit)}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="info-box" style={{ backgroundColor: '#E8F5E9', padding: '20px', borderRadius: '8px' }}>
                    <div className="info-label">Available Credit</div>
                    <div className="info-value" style={{ fontSize: '24px', fontWeight: '600', color: '#4CAF50' }}>
                      ₹{formatAmount(formData.availableCredit)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveProfile}
                >
                  <i className="fa fa-save"></i> Save Changes
                </button>
                <button 
                  className="btn btn-default"
                  onClick={handleCancel}
                  style={{ marginLeft: '10px' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Payment Settings Tab */}
        {activeTab === 'payment' && (
          <div className="settings-section">
            <h5 style={{ fontWeight: '600', marginBottom: '20px' }}>Payment Preferences</h5>

            <div className="form-group">
              <label>Default Payment Terms</label>
              <select
                name="defaultPaymentTerms"
                className="form-control"
                value={paymentSettings.defaultPaymentTerms}
                onChange={handleSettingChange}
                style={{ maxWidth: '300px' }}
              >
                <option value="30">30 Days</option>
                <option value="45">45 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
              </select>
            </div>

            <hr />

            <h5 style={{ fontWeight: '600', marginBottom: '15px' }}>Auto-Approval Settings</h5>
            
            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="autoApprovalEnabled"
                  checked={paymentSettings.autoApprovalEnabled}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>Enable Auto-Approval for Small Invoices</span>
              </label>
            </div>

            {paymentSettings.autoApprovalEnabled && (
              <div className="form-group" style={{ marginLeft: '30px' }}>
                <label>Auto-Approval Limit</label>
                <div className="input-group" style={{ maxWidth: '300px' }}>
                  <span className="input-group-addon">₹</span>
                  <input
                    type="number"
                    name="autoApprovalLimit"
                    className="form-control"
                    value={paymentSettings.autoApprovalLimit}
                    onChange={handleSettingChange}
                  />
                </div>
                <small className="text-muted">
                  Invoices below this amount will be auto-approved
                </small>
              </div>
            )}

            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-primary">
                <i className="fa fa-save"></i> Save Settings
              </button>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="settings-section">
            <h5 style={{ fontWeight: '600', marginBottom: '20px' }}>Notification Preferences</h5>

            <h6 style={{ fontWeight: '600', marginTop: '20px', marginBottom: '15px' }}>
              Notification Channels
            </h6>

            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={paymentSettings.emailNotifications}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>Email Notifications</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="smsNotifications"
                  checked={paymentSettings.smsNotifications}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>SMS Notifications</span>
              </label>
            </div>

            <hr />

            <h6 style={{ fontWeight: '600', marginTop: '20px', marginBottom: '15px' }}>
              Alert Types
            </h6>

            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="invoiceSubmissionAlert"
                  checked={paymentSettings.invoiceSubmissionAlert}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>New Invoice Submission</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="approvalReminder"
                  checked={paymentSettings.approvalReminder}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>Approval Reminders (Invoices pending &gt;3 days)</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox">
                <input
                  type="checkbox"
                  name="paymentDueAlert"
                  checked={paymentSettings.paymentDueAlert}
                  onChange={handleSettingChange}
                />
                <span style={{ marginLeft: '8px' }}>Payment Due Alerts</span>
              </label>
            </div>

            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-primary">
                <i className="fa fa-save"></i> Save Preferences
              </button>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="settings-section">
            <h5 style={{ fontWeight: '600', marginBottom: '20px' }}>Security Settings</h5>

            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter current password"
                style={{ maxWidth: '400px' }}
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter new password"
                style={{ maxWidth: '400px' }}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Confirm new password"
                style={{ maxWidth: '400px' }}
              />
            </div>

            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-primary">
                <i className="fa fa-lock"></i> Change Password
              </button>
            </div>

            <hr style={{ marginTop: '30px' }} />

            <h5 style={{ fontWeight: '600', marginBottom: '15px' }}>Two-Factor Authentication</h5>
            <p className="text-muted">Add an extra layer of security to your account</p>
            
            <button className="btn btn-success">
              <i className="fa fa-shield"></i> Enable 2FA
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function
const formatAmount = (amount) => {
  if (amount >= 10000000) {
    return `${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `${(amount / 100000).toFixed(2)} L`;
  }
  return amount?.toLocaleString('en-IN') || '0';
};

export default AnchorSettings;

